import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Send, MessageCircle, ShieldCheck, ArrowLeft, RefreshCw, User as UserIcon, Mail, CheckCircle2, HandshakeIcon, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { AppShell } from '../components/Layout';


/**
 * Admin Support console. Two-pane layout:
 *   - Left: filterable list of threads (open / escalated / resolved).
 *   - Right: selected thread transcript + reply box + status controls +
 *     account actions (activate one-time / gold / comp / reset-password).
 *
 * The page auto-refreshes the currently-open thread every 20s so admin
 * doesn't need to manually reload to see new customer messages.
 */

function StatusBadge({ status }) {
  const map = {
    open: 'bg-primary/10 text-primary border-primary/30',
    escalated: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
    resolved: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  };
  return (
    <Badge variant="outline" className={cn('text-[10px] uppercase tracking-widest font-mono', map[status] || '')}>
      {status}
    </Badge>
  );
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function TranscriptBubble({ role, content }) {
  const isUser = role === 'user';
  const isAdmin = role === 'admin';
  return (
    <div className={cn('flex', isUser ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap',
          isUser
            ? 'bg-secondary text-foreground rounded-bl-sm'
            : isAdmin
              ? 'bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 rounded-br-sm'
              : 'bg-primary/10 border border-primary/20 text-foreground rounded-br-sm',
        )}
      >
        <div className={cn('text-[10px] font-mono uppercase tracking-[0.16em] mb-1',
          isUser ? 'text-muted-foreground' : isAdmin ? 'text-[hsl(var(--success))]' : 'text-primary')}
        >
          {isUser ? 'Customer' : isAdmin ? 'Admin' : 'AI assistant'}
        </div>
        {content}
      </div>
    </div>
  );
}

export default function AdminSupportPage() {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {thread, messages}
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const res = await api.get(`/admin/support/threads${params}`);
      setThreads(res.data?.threads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load threads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/support/threads/${id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load thread');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // Auto-refresh the selected thread every 20s
  useEffect(() => {
    if (!selectedId) return undefined;
    const t = setInterval(() => loadDetail(selectedId), 20000);
    return () => clearInterval(t);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail]);

  const counts = useMemo(() => {
    const c = { all: threads.length, open: 0, escalated: 0, resolved: 0 };
    threads.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [threads]);

  const submitReply = async () => {
    const body = reply.trim();
    if (!body || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/admin/support/threads/${selectedId}/reply`, { message: body });
      setReply('');
      await loadDetail(selectedId);
      await loadThreads();
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (!selectedId) return;
    setStatusBusy(true);
    try {
      await api.post(`/admin/support/threads/${selectedId}/status`, { status });
      await loadDetail(selectedId);
      await loadThreads();
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setStatusBusy(false);
    }
  };

  const performAction = async (action) => {
    if (!selectedId) return;
    if (!window.confirm(`Confirm: ${action.replace(/_/g, ' ')}?`)) return;
    setActionBusy(true);
    try {
      const res = await api.post(`/admin/support/threads/${selectedId}/action`, { action });
      toast.success(res.data?.summary || 'Action completed');
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-4 sm:p-6" data-testid="admin-support-page">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground text-sm inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Admin
            </Link>
            <div className="h-4 w-px bg-border" />
            <h1 className="text-2xl font-display font-semibold tracking-tight">Support conversations</h1>
          </div>
          <Button variant="outline" size="sm" onClick={loadThreads} disabled={loading} data-testid="admin-support-refresh">
            {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
            Refresh
          </Button>
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="admin-support-tabs">
            <TabsTrigger value="all" data-testid="admin-support-filter-all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="open" data-testid="admin-support-filter-open">Open ({counts.open || 0})</TabsTrigger>
            <TabsTrigger value="escalated" data-testid="admin-support-filter-escalated">Escalated ({counts.escalated || 0})</TabsTrigger>
            <TabsTrigger value="resolved" data-testid="admin-support-filter-resolved">Resolved ({counts.resolved || 0})</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid lg:grid-cols-[360px_1fr] gap-4 mt-4">
          {/* Thread list */}
          <Card className="border-border">
            <CardContent className="p-0">
              {threads.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {loading ? 'Loading…' : 'No conversations yet.'}
                </div>
              ) : (
                <ul className="divide-y divide-border" data-testid="admin-support-thread-list">
                  {threads.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(t.id)}
                        className={cn(
                          'w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors',
                          selectedId === t.id && 'bg-secondary',
                        )}
                        data-testid={`admin-support-thread-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-medium truncate">
                            {t.guest_name || t.guest_email || (t.user_id ? `User ${String(t.user_id).slice(0, 8)}` : 'Anonymous')}
                          </span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {t.first_message_snippet}
                        </div>
                        <div className="text-[10px] text-muted-foreground/70 mt-1 flex items-center gap-2">
                          {t.guest_email && <><Mail size={9} /> {t.guest_email}</>}
                          <span>· {relativeTime(t.updated_at)}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Detail pane */}
          <Card className="border-border min-h-[500px]">
            <CardContent className="p-0 flex flex-col h-full">
              {!selectedId ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8">
                  <div className="text-center space-y-2">
                    <MessageCircle size={28} className="mx-auto opacity-40" />
                    <p>Select a conversation to view the transcript.</p>
                  </div>
                </div>
              ) : detailLoading && !detail ? (
                <div className="flex-1 flex items-center justify-center p-8">
                  <Loader2 size={20} className="animate-spin text-muted-foreground" />
                </div>
              ) : detail ? (
                <>
                  {/* Detail header */}
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <UserIcon size={14} className="text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">
                          {detail.thread.guest_name || detail.thread.guest_email || 'Anonymous'}
                        </span>
                        <StatusBadge status={detail.thread.status} />
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {detail.thread.guest_email || '—'}
                        {detail.thread.user_id && <> · account <span className="font-mono">{String(detail.thread.user_id).slice(0, 8)}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Select
                        value={detail.thread.status}
                        onValueChange={setStatus}
                        disabled={statusBusy}
                      >
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="admin-support-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="escalated">Escalated</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Transcript */}
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background/30">
                    {detail.messages.map((m) => (
                      <TranscriptBubble key={m.id} role={m.role} content={m.content} />
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="border-t border-border p-3 space-y-3 bg-card">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground mr-1">
                        Actions
                      </span>
                      <Button variant="outline" size="sm" disabled={actionBusy} onClick={() => performAction('activate_one_time')} data-testid="admin-support-action-onetime">
                        <Zap size={12} className="mr-1" /> Activate Profile Success
                      </Button>
                      <Button variant="outline" size="sm" disabled={actionBusy} onClick={() => performAction('activate_gold')} data-testid="admin-support-action-gold">
                        <Zap size={12} className="mr-1" /> Activate ETL Plan
                      </Button>
                      <Button variant="outline" size="sm" disabled={actionBusy} onClick={() => performAction('activate_comp')} data-testid="admin-support-action-comp">
                        <ShieldCheck size={12} className="mr-1" /> Grant Comp
                      </Button>
                      <Button variant="outline" size="sm" disabled={actionBusy} onClick={() => performAction('reset_password')} data-testid="admin-support-action-reset">
                        Reset password
                      </Button>
                      {detail.thread.status !== 'resolved' && (
                        <Button variant="outline" size="sm" disabled={statusBusy} onClick={() => setStatus('resolved')} data-testid="admin-support-mark-resolved" className="ml-auto text-[hsl(var(--success))] border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/10">
                          <CheckCircle2 size={12} className="mr-1" /> Mark resolved
                        </Button>
                      )}
                    </div>

                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type a reply to the customer…"
                      rows={3}
                      className="text-sm resize-none"
                      disabled={sending}
                      data-testid="admin-support-reply-input"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <HandshakeIcon size={11} /> Customer sees this as a message from the Blayzrr team.
                      </span>
                      <Button onClick={submitReply} disabled={sending || !reply.trim()} data-testid="admin-support-reply-send">
                        {sending ? (
                          <><Loader2 size={14} className="animate-spin mr-1.5" /> Sending</>
                        ) : (
                          <><Send size={14} className="mr-1.5" /> Send reply</>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
